import { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import DynamicTable from '@atlaskit/dynamic-table';
import Link from '@atlaskit/link';
import Lozenge, { ThemeAppearance } from '@atlaskit/lozenge';
import Pagination from '@atlaskit/pagination';
import { Flex, Text, xcss } from '@atlaskit/primitives';

import { IntervalRenderer } from 'src/components/pipelines/common';
import { formatDistanceToNow } from 'src/components/pipelines/utils';
import { useAnalytics } from 'src/components/settings/analytics';
import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { useParams } from 'src/router/hooks';

import { useQueuedPullRequests } from '../hooks';
import { messages } from '../merge-queues.i18n';
import { useFocusedMergeQueue, useMergeQueuesActions } from '../provider';
import {
  MergeQueueActionModalType,
  MergeQueuePullRequestStatus,
} from '../types';

import { MergeQueuePullRequestActions } from './MergeQueueActions';
import { MergeQueuePullRequestSummary } from './MergeQueuePullRequestSummary';

const statusAppearance: {
  [k in MergeQueuePullRequestStatus]: ThemeAppearance;
} = {
  QUEUED: 'inprogress',
  PROCESSING: 'moved',
  MERGING: 'moved',
  DEQUEUING: 'moved',
  MERGED: 'success',
  DEQUEUED: 'removed',
  REMOVED: 'removed',
};

export const MergeQueuePullRequestsTable = ({
  isRenderedInSettings = false,
}: {
  isRenderedInSettings?: boolean;
}) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: `mergeQueuePullRequestsTable${
      isRenderedInSettings ? 'InSettings' : ''
    }`,
  });
  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const { formatMessage } = useIntl();
  const [, { setFocusedPullRequest, setMergeQueueActionModal }] =
    useMergeQueuesActions();
  const { repositoryOwner, repositorySlug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();
  const [page, setPage] = useState(1);
  const isRemoveFromQueueEnabled = useStatsigGate(
    StatsigFeatureKeys.isRemoveFromQueueEnabled
  );

  const {
    isQueuedPullRequestsLoading,
    mergeQueuePullRequests,
    queuedPullRequestsPages,
  } = useQueuedPullRequests({
    mergeQueueUuid: focusedMergeQueue?.uuid || '',
    owner: repositoryOwner,
    slug: repositorySlug,
    page: page.toString(),
  });

  const tableHead = useMemo(
    () => ({
      cells: [
        {
          key: 'pullRequestSummary',
          content: formatMessage(messages.pullRequestTableHeader),
          width: 52,
        },
        {
          key: 'pullRequestDateAdded',
          content: formatMessage(messages.dateAddedTableHeader),
          width: 18,
        },
        {
          key: 'pullRequestStatus',
          content: formatMessage(messages.statusTableHeader),
          width: 14,
        },
        {
          key: 'pullRequestPipeline',
          content: formatMessage(messages.pipelineTableHeader),
          width: 10,
        },
        ...(isRemoveFromQueueEnabled || isRenderedInSettings
          ? [
              {
                key: 'pullRequestActions',
                content: formatMessage(messages.actionsTableHeader),
                width: 6,
              },
            ]
          : []),
      ],
    }),
    [formatMessage, isRemoveFromQueueEnabled, isRenderedInSettings]
  );

  const tableRows = useMemo(() => {
    return mergeQueuePullRequests
      .filter(mergeQueuePr => mergeQueuePr.pullrequest)
      .map(mergeQueuePr => {
        const pullRequest = mergeQueuePr.pullrequest;
        return {
          key: `pullRequest-${pullRequest.id}`,
          cells: [
            {
              key: `pullRequestSummary-${pullRequest.id}`,
              content: (
                <MergeQueuePullRequestSummary pullRequest={pullRequest} />
              ),
            },
            {
              key: `pullRequestDateAdded-${pullRequest.id}`,
              content: (
                <IntervalRenderer interval={15000}>
                  {() => formatDistanceToNow(pullRequest.created_on)}
                </IntervalRenderer>
              ),
            },
            {
              key: `pullRequestStatus-${pullRequest.id}`,
              content: (
                <Lozenge
                  appearance={
                    statusAppearance[mergeQueuePr.pr_status || 'QUEUED']
                  }
                  isBold
                >
                  {mergeQueuePr.pr_status || 'QUEUED'}
                </Lozenge>
              ),
            },
            {
              key: `pullRequestPipeline-${pullRequest.id}`,
              content: (
                <Link
                  href={`/${repositoryOwner}/${repositorySlug}/pipelines/results/${
                    mergeQueuePr.build_number || ''
                  }`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {`#${mergeQueuePr.build_number || ''}`}
                </Link>
              ),
            },
            ...(isRemoveFromQueueEnabled || isRenderedInSettings
              ? [
                  {
                    key: `pullRequestActions-${pullRequest.id}`,
                    content: (
                      <Flex alignItems="center" justifyContent="space-around">
                        <MergeQueuePullRequestActions
                          onDequeueClick={() => {
                            publishButtonClickEvent('removeFromQueue');
                            setFocusedPullRequest(pullRequest.id);
                            setMergeQueueActionModal(
                              MergeQueueActionModalType.DEQUEUE_PR
                            );
                          }}
                          onMoveToTopClick={
                            isRenderedInSettings
                              ? () => {
                                  publishButtonClickEvent('moveToTop');
                                  setFocusedPullRequest(pullRequest.id);
                                  setMergeQueueActionModal(
                                    MergeQueueActionModalType.MOVE_TO_TOP_PR
                                  );
                                }
                              : undefined
                          }
                        />
                      </Flex>
                    ),
                  },
                ]
              : []),
          ],
        };
      });
  }, [
    mergeQueuePullRequests,
    repositoryOwner,
    repositorySlug,
    publishButtonClickEvent,
    setFocusedPullRequest,
    setMergeQueueActionModal,
    isRemoveFromQueueEnabled,
    isRenderedInSettings,
  ]);

  if (!repositoryOwner || !repositorySlug || !focusedMergeQueue) return null;

  return (
    <>
      <DynamicTable
        emptyView={<Text>{formatMessage(messages.noPullRequestsQueued)}</Text>}
        head={tableHead}
        rows={tableRows}
        isFixedSize
        isLoading={isQueuedPullRequestsLoading}
      />
      {queuedPullRequestsPages.length > 1 && (
        <Flex justifyContent="center" xcss={xcss({ width: '100%' })}>
          <Pagination
            pages={queuedPullRequestsPages}
            selectedIndex={page - 1}
            onChange={(_, selectedIndex) => setPage(selectedIndex)}
          />
        </Flex>
      )}
    </>
  );
};
